import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, ChartBarIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';

const FEATURE_TITLE = "Antimicrobial Resistance Analysis";
const FEATURE_DESCRIPTION = "Detection of resistance genes with our proprietary scoring system (0-100) and classification by antibiotic class for informed treatment decisions.";

const AntimicrobialResistancePage: React.FC = () => {
  return (
    <div className="py-16 bg-background">
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h1 
            className="text-4xl font-extrabold text-text tracking-tight sm:text-5xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {FEATURE_TITLE}
          </motion.h1>
          <motion.p 
            className="mt-4 text-xl text-text max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {FEATURE_DESCRIPTION}
          </motion.p>
        </div>

        <div className="mt-16">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <ShieldCheckIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Resistance Gene Detection</h3>
              <p className="text-text">
                Our platform identifies antimicrobial resistance genes with high accuracy, helping clinicians 
                understand the resistance profile of pathogens.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Detection of 500+ known resistance markers</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Genomic analysis of resistance mechanisms</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Regular updates to resistance database</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.5 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <ChartBarIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Proprietary Scoring System</h3>
              <p className="text-text">
                Our innovative 0-100 scoring system provides a clear measure of resistance levels, 
                helping clinicians quickly assess the effectiveness of antibiotics.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">0-100 resistance scoring for each antibiotic</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Visual representation of resistance patterns</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Historical tracking of resistance development</span>
                </li>
              </ul>
            </motion.div>

            <motion.div 
              className="bg-background-alt rounded-lg p-8 shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.7 }}
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary text-text-light mx-auto mb-4">
                <LightBulbIcon className="h-6 w-6" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-text text-center mb-4">Treatment Decision Support</h3>
              <p className="text-text">
                We classify resistance by antibiotic class to help clinicians make informed treatment 
                decisions based on likely effectiveness.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Classification by antibiotic class</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Antibiotic effectiveness prediction</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 h-5 w-5 text-primary">•</span>
                  <span className="ml-2 text-text">Alternative treatment suggestions</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>

        <motion.div 
          className="mt-16 bg-section-light p-8 rounded-lg shadow-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <h2 className="text-2xl font-bold text-text mb-4">Resistance Score Example</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-text mb-4">Resistance Score Interpretation</h3>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <div className="w-4 h-4 rounded-full bg-score-none mr-3"></div>
                  <span className="text-text"><strong>0-20:</strong> Minimal resistance - Highly effective treatment option</span>
                </li>
                <li className="flex items-center">
                  <div className="w-4 h-4 rounded-full bg-score-low mr-3"></div>
                  <span className="text-text"><strong>21-40:</strong> Low resistance - Effective treatment option</span>
                </li>
                <li className="flex items-center">
                  <div className="w-4 h-4 rounded-full bg-score-moderate mr-3"></div>
                  <span className="text-text"><strong>41-60:</strong> Moderate resistance - Consider alternative options</span>
                </li>
                <li className="flex items-center">
                  <div className="w-4 h-4 rounded-full bg-score-high mr-3"></div>
                  <span className="text-text"><strong>61-80:</strong> High resistance - Not recommended</span>
                </li>
                <li className="flex items-center">
                  <div className="w-4 h-4 rounded-full bg-score-critical mr-3"></div>
                  <span className="text-text"><strong>81-100:</strong> Complete resistance - Ineffective treatment</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text mb-4">Sample Resistance Profile</h3>
              <div className="space-y-4">
                <div>
                  <p className="font-medium text-text mb-1">Penicillins</p>
                  <div className="h-4 w-full bg-gray-200 rounded-full">
                    <div className="h-4 bg-score-high rounded-full" style={{ width: '75%' }}></div>
                  </div>
                  <p className="text-sm text-text mt-1">Score: 75 - High Resistance</p>
                </div>
                <div>
                  <p className="font-medium text-text mb-1">Cephalosporins</p>
                  <div className="h-4 w-full bg-gray-200 rounded-full">
                    <div className="h-4 bg-score-moderate rounded-full" style={{ width: '45%' }}></div>
                  </div>
                  <p className="text-sm text-text mt-1">Score: 45 - Moderate Resistance</p>
                </div>
                <div>
                  <p className="font-medium text-text mb-1">Fluoroquinolones</p>
                  <div className="h-4 w-full bg-gray-200 rounded-full">
                    <div className="h-4 bg-score-critical rounded-full" style={{ width: '90%' }}></div>
                  </div>
                  <p className="text-sm text-text mt-1">Score: 90 - Complete Resistance</p>
                </div>
                <div>
                  <p className="font-medium text-text mb-1">Aminoglycosides</p>
                  <div className="h-4 w-full bg-gray-200 rounded-full">
                    <div className="h-4 bg-score-none rounded-full" style={{ width: '15%' }}></div>
                  </div>
                  <p className="text-sm text-text mt-1">Score: 15 - Minimal Resistance</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-16 text-center">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              href="/contact"
              variant="primary"
              size="lg"
            >
              Schedule a Demo
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AntimicrobialResistancePage; 